import React, { Component } from "react";
import TaskBoardService from "../../services/taskboardservice";

// Task Board (Audit -> Task Board): today's pickup + delivery tasks per store in four states —
// done / open / overdue / cancelled — plus an Executive view (network KPIs, store league, 7-day
// trend). Driven by the read-only Admin_GetTaskBoard proc. Auto-refreshes every 60s.
const ORD = { overdue: 0, open: 1, done: 2, cancelled: 3 };
const TRIP_COLORS = ["#2563eb", "#128c4b", "#e8590c", "#8b5cf6", "#0891b2", "#be185d", "#a16207"];
// Trip-planner settings are editable by Admins only (role present in localStorage roleNames).
const IS_ADMIN = (localStorage.getItem("roleNames") || "").split(",").map((s) => s.trim().toLowerCase()).includes("admin");
const tripColor = (L) => TRIP_COLORS[(L.charCodeAt(0) - 65) % TRIP_COLORS.length];

// LOCAL calendar date (not UTC) so "today" matches the viewer's day, not an off-by-one at night.
function localDay(d) {
  const x = new Date(d || Date.now());
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 10);
}

export default class TaskBoard extends Component {
  constructor(props) {
    super(props);
    this.service = new TaskBoardService();
    this.state = { tab: "store", date: localDay(), data: null, loading: true, error: "", expanded: null, tripSel: null, settingsOpen: false, savingCfg: false, cfgForm: null, cfgMsg: "" };
    this.timer = null;
  }

  componentDidMount() {
    this.load();
    this.timer = setInterval(() => { if (this.isToday()) this.load(true); }, 60000);
  }
  componentWillUnmount() { if (this.timer) clearInterval(this.timer); }

  isToday() { return this.state.date === localDay(); }

  load = async (silent) => {
    if (!silent) this.setState({ loading: true, error: "" });
    // For today, send "" so the server (IST) decides its own today; otherwise send the picked date.
    const out = await this.service.getBoard(this.isToday() ? "" : this.state.date);
    if (out && out.network) this.setState({ data: out, loading: false, error: "" });
    else this.setState({ loading: false, error: (out && out.message) || "Could not load the task board." });
  };

  onDate = (e) => { this.setState({ date: e.target.value }, () => this.load()); };

  pct(n, d) { return d ? Math.round((n / d) * 100) : 0; }
  pct1(n, d) { return d ? (Math.round((n / d) * 1000) / 10) : 0; }

  // ---- quick-win helpers (on-time %, at-risk, trend, attention band) — all FE-only ----
  nowMin() { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); }
  slotEndMin(slot) { if (!slot) return null; const p = String(slot).split("-"); if (p.length < 2) return null; const e = p[1].split(":"); const m = (+e[0]) * 60 + (+e[1]); return isNaN(m) ? null : m; }
  // At-risk = an OPEN task whose promised slot ends within the next 60 min. Only meaningful for today.
  isAtRisk(t) { if (!this.isToday() || !t || t.st !== "open" || !t.slot) return false; const e = this.slotEndMin(t.slot); if (e == null) return false; const d = e - this.nowMin(); return d > 0 && d <= 60; }
  atRiskMap(d, byExec) { const m = {}; (d.tasks || []).forEach((t) => { if (this.isAtRisk(t)) { const k = byExec ? (t.exId || 0) : t.sid; m[k] = (m[k] || 0) + 1; } }); return m; }
  atRiskTotal(d) { return (d.tasks || []).reduce((a, t) => a + (this.isAtRisk(t) ? 1 : 0), 0); }
  // On-time % = done / (done + overdue). null when nothing has reached an end state yet.
  ot(done, overdue) { const b = (done || 0) + (overdue || 0); return b ? Math.round((done || 0) / b * 100) : null; }
  otCls(p) { return p >= 95 ? "tb-otok" : p >= 85 ? "tb-otwarn" : "tb-otbad"; }
  otWord(p) { return p >= 95 ? "on target" : p >= 85 ? "watch" : "below"; }

  renderOt(p) {
    if (p == null) return <div className="tb-ot"><div className="tb-otv tb-num">—</div></div>;
    return <div className="tb-ot"><div className="tb-otv tb-num">{p}%</div><span className={"tb-otpill " + this.otCls(p)}>{this.otWord(p)}</span></div>;
  }

  renderAttn(d) {
    const n = d.network;
    const ar = this.atRiskTotal(d);
    const unassigned = (d.tasks || []).filter((t) => !t.exId && t.st === "open").length;
    let worst = null;
    (d.stores || []).forEach((s) => { const p = this.ot(s.done, s.overdue); if (p != null && (s.total || 0) >= 4 && (!worst || p < worst.p)) worst = { name: s.short || s.name, p }; });
    const load = {}; (d.tasks || []).forEach((t) => { if (t.exId) { if (!load[t.exId]) load[t.exId] = { name: t.ex, live: 0 }; if (t.st === "open" || t.st === "overdue") load[t.exId].live++; } });
    let hi = null, lo = null; Object.keys(load).forEach((k) => { const e = load[k]; if (!hi || e.live > hi.live) hi = e; if (!lo || e.live < lo.live) lo = e; });
    if (!n.overdue && !ar && !unassigned) {
      return (<div className="tb-attn tb-attn-ok"><span className="tb-attn-t tb-okt"><span className="tb-dot" />All clear</span><span className="tb-chip">No overdue, at-risk or unassigned tasks right now.</span></div>);
    }
    return (
      <div className="tb-attn">
        <span className="tb-attn-t"><span className="tb-dot" />Needs attention now</span>
        <span className="tb-chip c-od"><b>{n.overdue}</b> overdue <span className="mini">· SLA missed</span></span>
        {this.isToday() ? <span className="tb-chip c-ar"><b>{ar}</b> at-risk <span className="mini">· slot ends &lt; 60 min</span></span> : null}
        <span className="tb-chip c-un"><b>{unassigned}</b> unassigned</span>
        {worst ? <span className="tb-chip c-worst">Worst: <b>{worst.name}</b> <span className="mini">{worst.p}% on-time</span></span> : null}
        {hi && lo && hi !== lo ? <span className="tb-chip c-load">Load: <b>{hi.name}</b> <span className="mini">{hi.live} live</span> · <b>{lo.name}</b> <span className="mini">{lo.live} live</span></span> : null}
      </div>
    );
  }

  renderTrend(d) {
    const tr = d.trend || [];
    if (!tr.length) return null;
    const mx = Math.max.apply(null, tr.map((x) => x.total || 0)) || 1;
    const lbl = (dt) => { if (!dt) return ""; if (dt === localDay()) return "Today"; const w = new Date(dt + "T00:00:00"); return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][w.getDay()] || ""; };
    const last = tr[tr.length - 1], prev = tr[tr.length - 2];
    const p2 = last ? this.ot(last.done, last.overdue) : null, p1 = prev ? this.ot(prev.done, prev.overdue) : null;
    const delta = (p1 != null && p2 != null) ? p2 - p1 : null;
    return (
      <div>
        <div className="tb-sech"><h2>7-day trend · on-time % and volume</h2>
          {delta != null ? <span className="tb-tdelta">On-time today <span style={{ color: delta >= 0 ? "var(--done)" : "var(--overdue)" }}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta)} pts</span> vs yesterday</span> : null}
        </div>
        <div className="tb-panel">
          <div className="tb-trend">
            {tr.map((x, i) => {
              const h = Math.round((x.total || 0) / mx * 100);
              const oh = x.total ? Math.round((x.overdue || 0) / x.total * 100) : 0;
              const p = this.ot(x.done, x.overdue);
              return (
                <div className="tb-tcol" key={i}>
                  <div className="tb-totpct">{p == null ? "—" : p + "%"}</div>
                  <div className="tb-tbar"><div className="tb-tfill" style={{ height: h + "%" }}><div className="tb-tover" style={{ height: oh + "%" }} /></div></div>
                  <div className="tb-tnum">{x.total}</div>
                  <div className="tb-tlab">{lbl(x.date)}</div>
                </div>
              );
            })}
          </div>
          <div className="tb-tleg"><span><i className="tb-sw tb-trendbar" />Total tasks</span><span><i className="tb-sw tb-overdue" />Overdue share</span><span>% = on-time rate that day</span></div>
        </div>
      </div>
    );
  }

  fmtMin(m) { m = Math.round(m); return m >= 60 ? (Math.floor(m / 60) + "h" + (m % 60 ? " " + (m % 60) + "m" : "")) : (m + " min"); }
  // Trip-planner tunables from the proc payload (lk_tat_policy), with safe defaults.
  cfg() {
    const c = (this.state.data && this.state.data.config) || {};
    return {
      svcDelivery: +c.svcDelivery || 10, svcPickup: +c.svcPickup || 15, gap: +c.gap || 20,
      radiusKm: +c.radiusKm || 1.5, maxDelivery: +c.maxDelivery || 2, maxPickup: +c.maxPickup || 2, shiftHours: +c.shiftHours || 10,
    };
  }
  openSettings = () => this.setState({ settingsOpen: true, cfgForm: this.cfg(), cfgMsg: "" });
  cfgField = (k) => (e) => { const v = e.target.value; this.setState((s) => ({ cfgForm: Object.assign({}, s.cfgForm, { [k]: v }) })); };
  saveCfg = async () => {
    const f = this.state.cfgForm;
    this.setState({ savingCfg: true, cfgMsg: "" });
    const payload = { SvcDelivery: +f.svcDelivery, SvcPickup: +f.svcPickup, Gap: +f.gap, RadiusKm: +f.radiusKm, MaxDelivery: +f.maxDelivery, MaxPickup: +f.maxPickup, ShiftHours: +f.shiftHours };
    const res = await this.service.saveConfig(payload);
    if (res && (res.result === "Success" || res.statusCode === 200)) {
      this.setState({ savingCfg: false, settingsOpen: false });
      this.load();   // reload → fresh config + recomputed trips
    } else {
      this.setState({ savingCfg: false, cfgMsg: (res && res.message) || "Could not save settings." });
    }
  };
  renderSettings() {
    const f = this.state.cfgForm || this.cfg();
    const num = (k, label, step) => (
      <label className="tb-cfgf"><span>{label}</span><input type="number" step={step || "1"} min="0" value={f[k]} onChange={this.cfgField(k)} /></label>
    );
    return (
      <div className="tb-cfg">
        <div className="tb-cfgh">⚙ Trip planner settings <span className="tb-cfgsub">applies to the combine-nearby-stops suggestions</span></div>
        <div className="tb-cfgg">
          {num("svcDelivery", "Service min / delivery")}
          {num("svcPickup", "Service min / pickup")}
          {num("gap", "Gap between trips (min)")}
          {num("radiusKm", "Cluster radius (km)", "0.1")}
          {num("maxDelivery", "Max deliveries / trip")}
          {num("maxPickup", "Max pickups / trip")}
          {num("shiftHours", "Shift length (hours)")}
        </div>
        {this.state.cfgMsg ? <div className="tb-cfgerr">{this.state.cfgMsg}</div> : null}
        <div className="tb-cfga">
          <button className="tb-cfgsave" onClick={this.saveCfg} disabled={this.state.savingCfg}>{this.state.savingCfg ? "Saving…" : "Save settings"}</button>
          <button className="tb-cfgcancel" onClick={() => this.setState({ settingsOpen: false })} disabled={this.state.savingCfg}>Cancel</button>
        </div>
      </div>
    );
  }
  haversineKm(a, b) {
    const R = 6371, rad = Math.PI / 180;
    const dLa = (b[0] - a[0]) * rad, dLo = (b[1] - a[1]) * rad;
    const h = Math.sin(dLa / 2) ** 2 + Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLo / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }
  // Suggest combinable trips: cluster the ACTIVE (open/overdue) tasks of the SAME store whose
  // customer locations are within ~1.5 km of each other → one rider can do them in a single run.
  clusterTrips(tasks) {
    const C = this.cfg();
    const THRESH = C.radiusKm, MAXD = C.maxDelivery, MAXP = C.maxPickup;   // from admin settings (lk_tat_policy)
    const act = tasks.filter((t) => (t.st === "open" || t.st === "overdue") && t.cLat != null && t.cLng != null);
    const bySid = {};
    act.forEach((t) => { (bySid[t.sid] = bySid[t.sid] || []).push(t); });
    const clusters = [], label = {};
    let idx = 0;
    Object.keys(bySid).forEach((sid) => {
      // 1) geographic chained clusters — a stop joins if within THRESH of ANY member
      const rem = bySid[sid].slice();
      const geo = [];
      while (rem.length) {
        const grp = [rem.shift()];
        let added = true;
        while (added) {
          added = false;
          for (let i = rem.length - 1; i >= 0; i--) {
            if (grp.some((g) => this.haversineKm([g.cLat, g.cLng], [rem[i].cLat, rem[i].cLng]) <= THRESH)) { grp.push(rem[i]); rem.splice(i, 1); added = true; }
          }
        }
        if (grp.length >= 2) geo.push(grp);
      }
      // 2) nearest-neighbour routing + capacity: each trip <=2 del + <=2 pk; deliver out, pick up on return
      const ROADF = 1.4, SVC_D = C.svcDelivery, SVC_P = C.svcPickup;   // road factor const; service mins from settings
      geo.forEach((grp) => {
        const unv = grp.slice();
        const store = [grp[0].sLat, grp[0].sLng];
        while (unv.length) {
          // -- form a trip: greedily take the nearest stop that still fits capacity --
          let dc = 0, pc = 0, cur = store; const chosen = [];
          while (unv.length && !(dc >= MAXD && pc >= MAXP)) {
            let bi = -1, bd = Infinity;
            for (let i = 0; i < unv.length; i++) {
              if (unv[i].type === "delivery" ? dc >= MAXD : pc >= MAXP) continue;
              const d = this.haversineKm(cur, [unv[i].cLat, unv[i].cLng]);
              if (d < bd) { bd = d; bi = i; }
            }
            if (bi < 0) break;
            const t = unv.splice(bi, 1)[0]; chosen.push(t); cur = [t.cLat, t.cLng];
            if (t.type === "delivery") dc++; else pc++;
          }
          if (chosen.length < 2) continue;   // lone leftover stop (already removed) — nothing to combine
          // -- order the run: deliveries first (NN from store), then pickups (NN from last delivery) --
          const ordered = []; const pool = chosen.slice(); let pos = store;
          const pull = (pred) => { let bi = -1, bd = Infinity; for (let i = 0; i < pool.length; i++) { if (!pred(pool[i])) continue; const d = this.haversineKm(pos, [pool[i].cLat, pool[i].cLng]); if (d < bd) { bd = d; bi = i; } } if (bi < 0) return false; const t = pool.splice(bi, 1)[0]; pos = [t.cLat, t.cLng]; ordered.push(t); return true; };
          while (pool.some((t) => t.type === "delivery")) pull((t) => t.type === "delivery");
          while (pool.length) pull(() => true);
          const nd = ordered.filter((t) => t.type === "delivery").length, np = ordered.length - nd;
          // -- route km (store->first + inter-stop road-est + last->store) & live-traffic-informed pace --
          let km = (ordered[0].dist != null ? ordered[0].dist : ROADF * this.haversineKm(store, [ordered[0].cLat, ordered[0].cLng]));
          for (let i = 1; i < ordered.length; i++) km += ROADF * this.haversineKm([ordered[i - 1].cLat, ordered[i - 1].cLng], [ordered[i].cLat, ordered[i].cLng]);
          const lastT = ordered[ordered.length - 1];
          km += (lastT.dist != null ? lastT.dist : ROADF * this.haversineKm(store, [lastT.cLat, lastT.cLng]));
          let rs = 0, rn = 0; ordered.forEach((t) => { if (t.dist > 0 && t.eta > 0) { rs += t.eta / t.dist; rn++; } });
          const pace = rn ? rs / rn : 3.0;   // min per km (from live two-wheeler ETAs; fallback ~20 km/h)
          const travel = km * pace, service = nd * SVC_D + np * SVC_P, mins = Math.round(travel + service);
          let spread = 0; for (let i = 0; i < ordered.length; i++) for (let j = i + 1; j < ordered.length; j++) spread = Math.max(spread, this.haversineKm([ordered[i].cLat, ordered[i].cLng], [ordered[j].cLat, ordered[j].cLng]));
          let url = null;
          if (ordered[0].sLat != null) { const org = ordered[0].sLat + "," + ordered[0].sLng; url = "https://www.google.com/maps/dir/?api=1&origin=" + org + "&destination=" + org + "&waypoints=" + ordered.map((t) => t.cLat + "," + t.cLng).join("|") + "&travelmode=two-wheeler"; }
          const L = String.fromCharCode(65 + idx); idx++;
          ordered.forEach((t) => { label[t.type + t.id] = L; });
          clusters.push({ label: L, tasks: ordered, nd, np, spread, km, travel: Math.round(travel), service, mins, url });
        }
      });
    });
    return { clusters, label };
  }

  render() {
    const { tab, data, loading, error } = this.state;
    return (
      <div className="tb-root">
        <style>{CSS}</style>
        <div className="tb-head">
          <div className="tb-h">
            <span className="tb-dot" />
            <div>
              <div className="tb-title">Task Board</div>
              <div className="tb-sub">Pickups &amp; deliveries by store · done / open / overdue / cancelled</div>
            </div>
          </div>
          <div className="tb-ctrls">
            <input type="date" id="tb-date" className="tb-date" value={this.state.date} max={localDay()} onChange={this.onDate} />
            <button className={"tb-refreshbtn" + (loading ? " tb-spin" : "")} onClick={() => this.load()} disabled={loading} title="Refresh now">
              <span className="tb-ric">↻</span> {loading ? "Refreshing…" : "Refresh"}
            </button>
            {data ? <span className="tb-asat"><span className="tb-pulse" />as at {data.asOf}</span> : null}
            {IS_ADMIN ? <button className="tb-gear" onClick={this.openSettings} title="Trip planner settings (admin)">⚙</button> : null}
          </div>
        </div>

        {this.state.settingsOpen && IS_ADMIN ? this.renderSettings() : null}
        {data ? this.renderAttn(data) : null}

        <div className="tb-tabs">
          <button className={"tb-tab " + (tab === "store" ? "on" : "")} onClick={() => this.setState({ tab: "store" })}>By store</button>
          <button className={"tb-tab " + (tab === "exec" ? "on" : "")} onClick={() => this.setState({ tab: "exec" })}>By executive</button>
        </div>

        {loading && !data ? <div className="tb-msg">Loading task board…</div> : null}
        {error ? <div className="tb-err">{error}</div> : null}
        {data ? (tab === "store" ? this.renderStore(data) : this.renderExec(data)) : null}
      </div>
    );
  }

  kpi(lab, val, cls, foot) {
    return (
      <div className={"tb-kpi " + (cls || "")}>
        <div className="tb-kl">{lab}</div>
        <div className="tb-kv tb-num">{val}</div>
        {foot ? <div className="tb-kf">{foot}</div> : null}
      </div>
    );
  }

  bar(s, ar) {
    ar = Math.min(ar || 0, s.open || 0);            // at-risk is a subset of open
    const t = s.total || 1;
    const openShown = Math.max((s.open || 0) - ar, 0);
    const seg = (w, c) => w ? <i className={"tb-seg tb-" + c} style={{ width: (w / t * 100) + "%" }} title={c + " " + w} /> : null;
    return (
      <div className="tb-bar">
        {seg(s.done, "done")}{seg(ar, "atrisk")}{seg(openShown, "open")}{seg(s.overdue, "overdue")}{seg(s.cancelled, "cancelled")}
      </div>
    );
  }

  renderStore(d) {
    const n = d.network;
    const arMap = this.atRiskMap(d, false);
    const otKey = (s) => { const p = this.ot(s.done, s.overdue); return p == null ? 101 : p; };   // null → sort last
    const stores = (d.stores || []).slice().sort((a, b) =>
      (b.overdue - a.overdue) || ((arMap[b.id] || 0) - (arMap[a.id] || 0)) || (otKey(a) - otKey(b)) || (b.total - a.total));
    return (
      <div>
        <div className="tb-kpis">
          {this.kpi("On-time %", (this.ot(n.done, n.overdue) == null ? "—" : this.ot(n.done, n.overdue) + "%"), "k-ontime", "target 95%")}
          {this.kpi("Overdue", n.overdue, "k-overdue", "SLA missed")}
          {this.kpi("At-risk", this.atRiskTotal(d), "k-atrisk", "next 60 min")}
          {this.kpi("Open", n.open, "k-open", this.pct(n.open, n.total) + "% of today")}
          {this.kpi("Done", n.done, "k-done", this.pct(n.done, n.total) + "% of today")}
          {this.kpi("Total tasks", n.total, "k-total", stores.length + " stores")}
        </div>
        {this.renderTrend(d)}
        <div className="tb-sech">
          <h2>By store · act on overdue &amp; at-risk first</h2>
          <div className="tb-leg">
            <span><i className="tb-sw tb-done" />Done</span>
            <span><i className="tb-sw tb-atrisk" />At-risk</span>
            <span><i className="tb-sw tb-open" />Open</span>
            <span><i className="tb-sw tb-overdue" />Overdue</span>
          </div>
        </div>
        <div className="tb-list">
          <div className="tb-row tb-rhead">
            <div>Store</div><div>Progress &amp; counts</div><div className="tb-r">On-time</div><div>Pickup / Delivery</div><div className="tb-r">Total</div>
          </div>
          {stores.map((s) => {
            const exp = this.state.expanded === "s" + s.id;
            const ar = arMap[s.id] || 0;
            return (
            <React.Fragment key={s.id}>
            <div className={"tb-row tb-click" + ((s.overdue > 0 || ar > 0) ? " tb-hot" : "") + (exp ? " tb-open-row" : "")}
                 onClick={() => this.setState({ expanded: exp ? null : "s" + s.id, tripSel: null })}>
              <div className="tb-store">
                <div className="tb-nm"><span className={"tb-chev" + (exp ? " tb-chev-o" : "")}>▸</span>{s.name}{s.short ? <span className="tb-tag">{s.short}</span> : null}</div>
                <div className="tb-meta">{(s.pk.open + s.pk.overdue + s.pk.done + s.pk.cancelled)} pickups · {(s.dl.open + s.dl.overdue + s.dl.done + s.dl.cancelled)} deliveries · tap to see tasks</div>
              </div>
              <div className="tb-barwrap">
                {this.bar(s, ar)}
                <div className="tb-counts">
                  {s.overdue ? <span className="tb-coverdue tb-num"><b>{s.overdue}</b> overdue</span> : null}
                  {ar ? <span className="tb-catrisk tb-num"><b>{ar}</b> at-risk</span> : null}
                  <span className="tb-copen tb-num"><b>{s.open}</b> open</span>
                  <span className="tb-cdone tb-num"><b>{s.done}</b> done</span>
                  {s.cancelled ? <span className="tb-ccanc tb-num"><b>{s.cancelled}</b> cancelled</span> : null}
                </div>
              </div>
              {this.renderOt(this.ot(s.done, s.overdue))}
              <div className="tb-split">
                <div><span className="tb-sk">Pickups</span><span className="tb-num">{s.pk.overdue ? <span className="tb-xo">{s.pk.overdue} od</span> : null} {s.pk.open} open · {s.pk.done} done</span></div>
                <div><span className="tb-sk">Delivery</span><span className="tb-num">{s.dl.overdue ? <span className="tb-xo">{s.dl.overdue} od</span> : null} {s.dl.open} open · {s.dl.done} done</span></div>
              </div>
              <div className="tb-tot tb-num">{s.total}</div>
            </div>
            {exp ? this.renderDrill(d, "store", s.id) : null}
            </React.Fragment>
          );})}
        </div>
        <div className="tb-foot">On-time % = done ÷ (done + overdue), target 95% · At-risk = open task whose promised slot ends within 60 min (act before it breaches) · Overdue = still open past its slot end · Done = picked up/delivered. Refreshes every 60s while viewing today.</div>
      </div>
    );
  }

  renderDrill(d, mode, id) {
    const byStore = mode === "store";
    const tasks = (d.tasks || []).filter((t) => (byStore ? t.sid === id : (t.exId || 0) === id) && t.st !== "cancelled")
      .sort((a, b) => {
        const da = a.dist == null ? Infinity : a.dist;
        const db = b.dist == null ? Infinity : b.dist;
        return (da - db) || (ORD[a.st] - ORD[b.st]) || (a.slot || "").localeCompare(b.slot || "") || (a.type < b.type ? -1 : 1);
      });
    const slot = (s) => (s ? s.slice(0, 5) + "–" + s.slice(9, 14) : "—");
    const sname = {};
    (d.stores || []).forEach((s) => { sname[s.id] = s.short || s.name; });
    const trip = this.clusterTrips(tasks);
    const sel = this.state.tripSel;                                   // selected combined-trip (chip click)
    const selC = sel ? trip.clusters.find((x) => x.label === sel) : null;
    const shown = selC ? selC.tasks : (sel ? [] : tasks);            // trip tasks are in route order (deliveries first, then pickups)
    return (
      <div className="tb-drill" key={(byStore ? "ds" : "de") + id}>
        {trip.clusters.length ? (() => {
          const CF = this.cfg();
          const GAP = CF.gap, shiftMin = CF.shiftHours * 60;   // from admin settings
          const gaps = Math.max(0, trip.clusters.length - 1) * GAP;
          const totMin = trip.clusters.reduce((a, c) => a + c.mins, 0) + gaps;
          return (
          <div className="tb-combine">
            <span className="tb-cbhdr" title="Nearby active stops a rider can do in one run — capped at 2 deliveries + 2 pickups (carrying capacity). Trip time = travel (live two-wheeler) + 10 min/delivery + 15 min/pickup. Shift total adds a 20 min gap between trips.">💡 Combine nearby stops</span>
            <span className="tb-cbsum">{trip.clusters.length} trip{trip.clusters.length > 1 ? "s" : ""} · ~{this.fmtMin(totMin)} of a {CF.shiftHours}h shift ({Math.round(totMin / shiftMin * 100)}%){gaps ? " · incl " + gaps + "m between trips" : ""}</span>
            {trip.clusters.map((c) => (
              <span className={"tb-cbchip" + (this.state.tripSel === c.label ? " on" : "")} key={c.label}
                    title={"Click to show just this trip's stops · Travel ~" + this.fmtMin(c.travel) + " (~" + c.km.toFixed(1) + " km) + service " + c.service + " min (" + c.nd + "×10 + " + c.np + "×15)"}
                    onClick={() => this.setState({ tripSel: this.state.tripSel === c.label ? null : c.label })}>
                <span className="tb-cblab" style={{ background: tripColor(c.label) }}>{c.label}</span>
                <b>~{this.fmtMin(c.mins)}</b> · {c.nd} del + {c.np} pk · {c.tasks.map((t) => "#" + t.id).join(" → ")}
                {c.url ? <a className="tb-cbroute" href={c.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>route ↗</a> : null}
              </span>
            ))}
          </div>
          );
        })() : null}
        {sel ? (
          <div className="tb-tripsel">
            <span className="tb-cblab" style={{ background: tripColor(sel) }}>{sel}</span>
            Showing trip <b>{sel}</b>’s stops in route order ({shown.length})
            <button className="tb-tripclear" onClick={() => this.setState({ tripSel: null })}>show all ✕</button>
          </div>
        ) : null}
        <table className="tb-dtbl">
          <thead><tr><th>Type</th><th className="tb-r">Task #</th><th>Customer</th><th>Area · Pin</th><th className="tb-r">Dist</th><th>Slot</th><th>{byStore ? "Executive" : "Store"}</th><th>Status</th></tr></thead>
          <tbody>
            {shown.map((t) => (
              <tr key={t.type + t.id}>
                <td>{t.type === "pickup" ? "Pickup" : "Delivery"}</td>
                <td className="tb-r tb-num">{t.id}{trip.label[t.type + t.id] ? <span className="tb-triptag" style={{ background: tripColor(trip.label[t.type + t.id]) }} title={"Combined trip " + trip.label[t.type + t.id] + " — click to show just this trip"} onClick={(e) => { e.stopPropagation(); const L = trip.label[t.type + t.id]; this.setState({ tripSel: this.state.tripSel === L ? null : L }); }}>{trip.label[t.type + t.id]}</span> : null}</td>
                <td>{t.cust || "—"}{t.mob ? <a className="tb-mob tb-moblink" href={"/store/storeorderhistory?mobile=" + t.mob} target="_blank" title="Search order history for this number" onClick={(e) => e.stopPropagation()}>{t.mob} ↗</a> : null}</td>
                <td>{t.area || "—"}{t.pin ? <span className="tb-pin">{t.pin}</span> : null}</td>
                <td className="tb-num tb-r">{t.dist != null ? (() => {
                  const inner = <>{t.dist.toFixed(1)} km{t.eta != null ? " · " + t.eta + " min" : ""}{t.etaLive ? <span className="tb-live" title="Live-traffic ETA from the store (current conditions)"><span className="tb-livedot" />live</span> : null}</>;
                  return t.map
                    ? <a className="tb-maplink" href={t.map} target="_blank" rel="noopener noreferrer" title="Open store→customer route in Google Maps (live traffic)" onClick={(e) => e.stopPropagation()}>{inner} <span className="tb-mapico">↗</span></a>
                    : inner;
                })() : "—"}</td>
                <td className="tb-num">{slot(t.slot)}</td>
                <td>{byStore ? (t.ex ? t.ex : <span className="tb-unassigned">Unassigned</span>) : (sname[t.sid] || t.sid)}</td>
                <td><span className={"tb-stpill tb-st-" + t.st}>{t.st}</span></td>
              </tr>
            ))}
            {shown.length === 0 ? <tr><td colSpan="8" className="tb-empty">{sel ? "No stops in this trip." : "No tasks."}</td></tr> : null}
          </tbody>
        </table>
      </div>
    );
  }

  renderExec(d) {
    const arMap = this.atRiskMap(d, true);
    const map = {};
    (d.tasks || []).forEach((t) => {
      const key = t.exId || 0;
      if (!map[key]) map[key] = { id: t.exId, name: t.ex || "Unassigned", total: 0, open: 0, overdue: 0, done: 0, cancelled: 0, pk: 0, dl: 0, stores: new Set() };
      const m = map[key]; m.total++; m[t.st] = (m[t.st] || 0) + 1; if (t.type === "pickup") m.pk++; else m.dl++; m.stores.add(t.sid);
    });
    const otKey = (e) => { const p = this.ot(e.done, e.overdue); return p == null ? 101 : p; };
    const execs = Object.values(map).sort((a, b) =>
      (b.overdue - a.overdue) || ((arMap[b.id || 0] || 0) - (arMap[a.id || 0] || 0)) || (otKey(a) - otKey(b)) || (b.total - a.total));
    const n = d.network;
    return (
      <div>
        <div className="tb-kpis">
          {this.kpi("On-time %", (this.ot(n.done, n.overdue) == null ? "—" : this.ot(n.done, n.overdue) + "%"), "k-ontime", "target 95%")}
          {this.kpi("Overdue", n.overdue, "k-overdue", "across all")}
          {this.kpi("At-risk", this.atRiskTotal(d), "k-atrisk", "next 60 min")}
          {this.kpi("Open", n.open, "k-open", "still to do")}
          {this.kpi("Done", n.done, "k-done", "")}
          {this.kpi("Unassigned", map[0] ? map[0].total : 0, "k-cancelled", "no executive")}
        </div>
        {this.renderTrend(d)}
        <div className="tb-sech">
          <h2>By executive · on-time &amp; current load</h2>
          <div className="tb-leg">
            <span><i className="tb-sw tb-done" />Done</span>
            <span><i className="tb-sw tb-atrisk" />At-risk</span>
            <span><i className="tb-sw tb-open" />Open</span>
            <span><i className="tb-sw tb-overdue" />Overdue</span>
          </div>
        </div>
        <div className="tb-list">
          <div className="tb-row tb-rhead"><div>Executive</div><div>Progress &amp; counts</div><div className="tb-r">On-time</div><div>Live load</div><div className="tb-r">Total</div></div>
          {execs.map((e) => {
            const key = "e" + (e.id || 0);
            const exp = this.state.expanded === key;
            const ar = arMap[e.id || 0] || 0;
            const live = e.open + e.overdue;
            return (
            <React.Fragment key={e.id || "un"}>
            <div className={"tb-row tb-click" + ((e.overdue > 0 || ar > 0) ? " tb-hot" : "") + (exp ? " tb-open-row" : "")}
                 onClick={() => this.setState({ expanded: exp ? null : key, tripSel: null })}>
              <div className="tb-store"><div className="tb-nm"><span className={"tb-chev" + (exp ? " tb-chev-o" : "")}>▸</span>{e.name}{!e.id ? <span className="tb-tag tb-tagun">unassigned</span> : null}</div><div className="tb-meta">{e.pk} pk · {e.dl} dl · {e.stores.size} store{e.stores.size > 1 ? "s" : ""}</div></div>
              <div className="tb-barwrap">
                {this.bar(e, ar)}
                <div className="tb-counts">
                  {e.overdue ? <span className="tb-coverdue tb-num"><b>{e.overdue}</b> overdue</span> : null}
                  {ar ? <span className="tb-catrisk tb-num"><b>{ar}</b> at-risk</span> : null}
                  <span className="tb-copen tb-num"><b>{e.open}</b> open</span>
                  <span className="tb-cdone tb-num"><b>{e.done}</b> done</span>
                  {e.cancelled ? <span className="tb-ccanc tb-num"><b>{e.cancelled}</b> cancelled</span> : null}
                </div>
              </div>
              {e.id ? this.renderOt(this.ot(e.done, e.overdue)) : <div className="tb-ot"><div className="tb-otv tb-num">—</div></div>}
              <div className="tb-split"><div className="tb-num">{live} live {live >= 10 ? <span className="tb-xo">overloaded</span> : (e.id && live <= 2 ? <span className="tb-light">light</span> : null)}</div></div>
              <div className="tb-tot tb-num">{e.total}</div>
            </div>
            {exp ? this.renderDrill(d, "exec", e.id || 0) : null}
            </React.Fragment>
          );})}
        </div>
        <div className="tb-foot">On-time % = done ÷ (done + overdue) per executive · "Live load" = open + overdue in hand (overloaded ≥ 10, light ≤ 2) to spot who to rebalance to · "Unassigned" = no executive set yet.</div>
      </div>
    );
  }
}

const CSS = `
.tb-root{--bg:#f6f2ee;--card:#fff;--ink:#241326;--muted:#8a7d90;--line:#ece4ec;--soft:#faf6f9;
  --pink:#fe1d7f;--pinkink:#c70d63;--done:#128c4b;--donebg:rgba(18,140,75,.14);
  --open:#2563eb;--openbg:rgba(37,99,235,.12);--overdue:#e8590c;--overduebg:rgba(232,89,12,.14);
  --atrisk:#e0a500;--atriskbg:rgba(224,165,0,.16);
  --cancel:#9a8da1;--cancelbg:rgba(154,141,161,.16);--track:#efe7ee;--r:#d92d20;
  color:var(--ink);font-family:'IBM Plex Sans',system-ui,-apple-system,sans-serif;padding:0 20px 44px}
.tb-num{font-variant-numeric:tabular-nums}
.tb-head{display:flex;align-items:center;gap:14px;flex-wrap:wrap;padding:18px 0 6px}
.tb-h{display:flex;align-items:center;gap:11px}
.tb-dot{width:11px;height:11px;border-radius:50%;background:var(--pink);box-shadow:0 0 0 4px rgba(254,29,127,.18)}
.tb-title{font-size:21px;font-weight:800;letter-spacing:-.01em}
.tb-sub{color:var(--muted);font-size:12.5px;margin-top:1px}
.tb-ctrls{margin-left:auto;display:flex;align-items:center;gap:10px}
.tb-date{border:1px solid var(--line);background:var(--card);border-radius:9px;padding:7px 10px;font:inherit;color:var(--ink)}
.tb-refreshbtn{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line);background:var(--card);color:var(--ink);border-radius:9px;padding:7px 13px;font:inherit;font-weight:600;cursor:pointer}
.tb-refreshbtn:hover{border-color:var(--pink);color:var(--pinkink)}
.tb-refreshbtn[disabled]{opacity:.65;cursor:default}
.tb-ric{display:inline-block;font-size:15px;line-height:1}
.tb-spin .tb-ric{animation:tbspin .8s linear infinite}
@keyframes tbspin{to{transform:rotate(360deg)}}
.tb-asat{display:inline-flex;align-items:center;gap:7px;font-size:12px;color:var(--muted);background:var(--card);border:1px solid var(--line);border-radius:999px;padding:6px 12px}
.tb-pulse{width:7px;height:7px;border-radius:50%;background:var(--done)}
.tb-tabs{display:flex;gap:8px;border-bottom:1px solid var(--line);margin-top:8px}
.tb-tab{border:0;background:transparent;font:inherit;font-weight:700;color:var(--muted);padding:10px 4px;margin-right:14px;cursor:pointer;border-bottom:2px solid transparent}
.tb-tab.on{color:var(--pinkink);border-bottom-color:var(--pink)}
.tb-refresh{margin-left:auto;border:1px solid var(--line);background:var(--card);border-radius:8px;width:34px;cursor:pointer;color:var(--muted);font-size:16px;align-self:center}
.tb-msg{color:var(--muted);padding:40px 0;text-align:center}
.tb-err{background:#fff0ee;border:1px solid #f5c6c0;color:var(--r);border-radius:12px;padding:12px 16px;margin-top:14px;font-weight:600}

.tb-kpis{display:grid;grid-template-columns:repeat(6,1fr);gap:12px;margin-top:18px}
.tb-kpi{background:var(--card);border:1px solid var(--line);border-radius:14px;padding:14px 15px;box-shadow:0 1px 2px rgba(36,19,38,.04),0 6px 16px rgba(36,19,38,.05)}
.tb-kl{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);font-weight:600}
.tb-kv{font-family:'Archivo','IBM Plex Sans',sans-serif;font-weight:800;font-size:26px;line-height:1.05;margin-top:7px;letter-spacing:-.02em}
.tb-kf{font-size:12px;color:var(--muted);margin-top:5px}
.k-total{border-top:3px solid var(--pink)} .k-open .tb-kv{color:var(--open)} .k-open{border-top:3px solid var(--open)}
.k-done .tb-kv{color:var(--done)} .k-done{border-top:3px solid var(--done)}
.k-overdue .tb-kv{color:var(--overdue)} .k-overdue{border-top:3px solid var(--overdue)}
.k-cancelled .tb-kv{color:var(--cancel)} .k-cancelled{border-top:3px solid var(--cancel)}
.k-atrisk .tb-kv{color:var(--atrisk)} .k-atrisk{border-top:3px solid var(--atrisk)}
.k-ontime .tb-kv{color:var(--done)} .k-ontime{border-top:3px solid var(--done)}

.tb-sech{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin:24px 0 2px}
.tb-sech h2{font-size:13.5px;letter-spacing:.04em;text-transform:uppercase;color:var(--muted);font-weight:700;margin:0}
.tb-leg{display:flex;gap:14px;flex-wrap:wrap;font-size:12px;color:var(--muted)}
.tb-leg span{display:inline-flex;align-items:center;gap:6px}
.tb-sw{width:10px;height:10px;border-radius:3px;display:inline-block}
.tb-done{background:var(--done)} .tb-open{background:var(--open)} .tb-overdue{background:var(--overdue)} .tb-atrisk{background:var(--atrisk)} .tb-cancelled{background:var(--cancel)} .tb-trendbar{background:#c4b7cb}

.tb-list{background:var(--card);border:1px solid var(--line);border-radius:16px;box-shadow:0 1px 2px rgba(36,19,38,.04),0 6px 18px rgba(36,19,38,.05);margin-top:12px;overflow:hidden}
.tb-row{display:grid;grid-template-columns:186px 1fr 84px 150px 54px;gap:16px;align-items:center;padding:14px 18px;border-bottom:1px solid var(--line)}
.tb-row:last-child{border-bottom:0}
.tb-rhead{padding:10px 18px;background:var(--soft)}
.tb-rhead div{font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);font-weight:700}
.tb-hot{background:linear-gradient(90deg,rgba(232,89,12,.05),transparent 60%)}
.tb-store .tb-nm{font-weight:600;font-size:14.5px;display:flex;align-items:center;gap:8px}
.tb-tag{font-size:10px;font-weight:700;letter-spacing:.04em;color:var(--pinkink);background:rgba(254,29,127,.13);border-radius:5px;padding:1px 6px}
.tb-meta{font-size:12px;color:var(--muted);margin-top:3px}
.tb-bar{height:16px;border-radius:7px;background:var(--track);display:flex;overflow:hidden}
.tb-seg{display:block;height:100%}
.tb-seg.tb-done{background:var(--done)} .tb-seg.tb-atrisk{background:var(--atrisk)} .tb-seg.tb-open{background:var(--open)} .tb-seg.tb-overdue{background:var(--overdue)} .tb-seg.tb-cancelled{background:var(--cancel)}
.tb-counts{display:flex;gap:14px;font-size:12px;color:var(--muted);flex-wrap:wrap;margin-top:6px}
.tb-counts b{font-weight:700}
.tb-coverdue{color:var(--overdue)} .tb-copen{color:var(--open)} .tb-cdone{color:var(--done)} .tb-ccanc{color:var(--cancel)} .tb-catrisk{color:var(--atrisk)}
.tb-light{color:var(--open);font-weight:700;margin-left:4px}
.tb-ot{text-align:right}
.tb-otv{font-family:'Archivo',sans-serif;font-weight:800;font-size:18px;line-height:1}
.tb-otpill{display:inline-block;padding:1px 7px;border-radius:999px;font-size:10.5px;font-weight:700;margin-top:3px}
.tb-otok{color:var(--done);background:var(--donebg)} .tb-otwarn{color:#8a6d00;background:var(--atriskbg)} .tb-otbad{color:#fff;background:var(--overdue)}
.tb-attn{display:flex;gap:10px;flex-wrap:wrap;align-items:center;margin-top:16px;background:linear-gradient(90deg,rgba(232,89,12,.08),rgba(254,29,127,.05));border:1px solid rgba(232,89,12,.25);border-radius:14px;padding:11px 14px}
.tb-attn-ok{background:var(--donebg);border-color:rgba(18,140,75,.25)}
.tb-attn-t{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--overdue);font-weight:800;margin-right:2px;display:inline-flex;align-items:center;gap:7px}
.tb-attn-t .tb-dot{background:var(--overdue);box-shadow:0 0 0 4px rgba(232,89,12,.16);animation:tbpulse 1.6s infinite}
.tb-okt{color:var(--done)} .tb-okt .tb-dot{background:var(--done);box-shadow:0 0 0 4px rgba(18,140,75,.16);animation:none}
@keyframes tbpulse{0%,100%{opacity:1}50%{opacity:.45}}
.tb-chip{display:inline-flex;align-items:center;gap:6px;background:var(--card);border:1px solid var(--line);border-radius:999px;padding:5px 12px;font-size:12.5px;font-weight:600;color:var(--ink)}
.tb-chip b{font-family:'Archivo',sans-serif;font-size:15px}
.tb-chip.c-od b{color:var(--overdue)} .tb-chip.c-ar b{color:var(--atrisk)} .tb-chip.c-un b{color:var(--pinkink)} .tb-chip.c-worst b{color:var(--overdue)} .tb-chip.c-load b{color:var(--open)}
.tb-chip .mini{font-weight:400;color:var(--muted)}
.tb-totpct{font-size:11px;font-weight:800;color:var(--done)}
.tb-tdelta{font-size:12.5px;color:var(--muted);font-weight:600}
.tb-live{display:inline-flex;align-items:center;gap:3px;margin-left:6px;font-size:9px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:var(--done);background:var(--donebg);border-radius:4px;padding:1px 5px;vertical-align:middle}
.tb-livedot{width:5px;height:5px;border-radius:50%;background:var(--done);animation:tbpulse 1.4s infinite}
.tb-maplink{color:var(--pinkink);text-decoration:none;font-weight:600;cursor:pointer}
.tb-maplink:hover{text-decoration:underline}
.tb-mapico{font-weight:700}
.tb-combine{display:flex;flex-wrap:wrap;align-items:center;gap:8px;padding:10px 2px 12px;font-size:12px}
.tb-cbhdr{font-weight:800;color:var(--pinkink);font-size:11.5px;text-transform:uppercase;letter-spacing:.03em}
.tb-cbsum{font-size:12px;font-weight:700;color:var(--ink);background:var(--soft);border:1px solid var(--line);border-radius:999px;padding:4px 11px}
.tb-gear{border:1px solid var(--line);background:var(--card);color:var(--muted);border-radius:9px;width:36px;height:36px;font-size:16px;cursor:pointer}
.tb-gear:hover{border-color:var(--pink);color:var(--pinkink)}
.tb-cfg{background:var(--card);border:1px solid var(--line);border-radius:14px;padding:16px 18px;margin-top:14px;box-shadow:0 6px 18px rgba(36,19,38,.06)}
.tb-cfgh{font-weight:800;font-size:14px;margin-bottom:12px}
.tb-cfgsub{font-weight:500;font-size:12px;color:var(--muted);margin-left:6px}
.tb-cfgg{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}
.tb-cfgf{display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--muted);font-weight:600}
.tb-cfgf input{border:1px solid var(--line);background:var(--soft);border-radius:8px;padding:8px 10px;font:inherit;font-size:14px;color:var(--ink)}
.tb-cfgf input:focus{outline:none;border-color:var(--pink)}
.tb-cfga{display:flex;gap:10px;margin-top:14px}
.tb-cfgsave{border:0;background:var(--pink);color:#fff;border-radius:9px;padding:9px 18px;font:inherit;font-weight:700;cursor:pointer}
.tb-cfgsave[disabled]{opacity:.6;cursor:default}
.tb-cfgcancel{border:1px solid var(--line);background:var(--card);color:var(--ink);border-radius:9px;padding:9px 16px;font:inherit;font-weight:600;cursor:pointer}
.tb-cfgerr{color:var(--r);font-size:12.5px;font-weight:600;margin-top:10px}
@media(max-width:820px){.tb-cfgg{grid-template-columns:repeat(2,1fr)}}
.tb-cbchip{display:inline-flex;align-items:center;gap:7px;background:var(--card);border:1px solid var(--line);border-radius:999px;padding:4px 12px 4px 5px;color:var(--ink);font-weight:600;cursor:pointer;transition:box-shadow .12s,border-color .12s}
.tb-cbchip:hover{border-color:var(--pink)}
.tb-cbchip.on{border-color:var(--pink);box-shadow:0 0 0 2px rgba(254,29,127,.22)}
.tb-triptag{cursor:pointer}
.tb-tripsel{display:flex;align-items:center;gap:9px;margin:2px 2px 11px;font-size:12.5px;font-weight:600;color:var(--ink)}
.tb-tripclear{margin-left:4px;border:1px solid var(--line);background:var(--card);color:var(--muted);border-radius:999px;padding:3px 11px;font:inherit;font-size:11.5px;font-weight:700;cursor:pointer}
.tb-tripclear:hover{color:var(--ink);border-color:var(--pink)}
.tb-cblab{display:inline-flex;align-items:center;justify-content:center;width:19px;height:19px;border-radius:50%;color:#fff;font-weight:800;font-size:11px;flex:0 0 auto}
.tb-cbroute{color:var(--pinkink);text-decoration:none;font-weight:700;margin-left:3px}
.tb-cbroute:hover{text-decoration:underline}
.tb-triptag{display:inline-flex;align-items:center;justify-content:center;width:15px;height:15px;border-radius:50%;color:#fff;font-weight:800;font-size:9px;margin-left:6px;vertical-align:middle}
.tb-split{display:flex;flex-direction:column;gap:5px;font-size:12px;color:var(--muted)}
.tb-sk{display:inline-block;width:58px;color:var(--muted)}
.tb-split .tb-num{color:var(--ink)}
.tb-xo{color:var(--overdue);font-weight:700;margin-right:4px}
.tb-tot{font-family:'Archivo',sans-serif;font-weight:800;font-size:22px;text-align:right}
.tb-r{text-align:right}
.tb-foot{margin-top:18px;color:var(--muted);font-size:12.5px;line-height:1.6}

.tb-click{cursor:pointer}
.tb-click:hover{background:var(--soft)}
.tb-open-row{background:var(--soft)}
.tb-chev{display:inline-block;color:var(--muted);font-size:11px;margin-right:4px;transition:transform .15s}
.tb-chev-o{transform:rotate(90deg)}
.tb-drill{background:var(--soft);border-bottom:1px solid var(--line);padding:2px 18px 14px;overflow-x:auto}
.tb-dtbl{width:100%;border-collapse:collapse;font-size:12.5px;min-width:560px}
.tb-dtbl th{font-size:10px;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);font-weight:700;text-align:left;padding:9px 8px 6px;border-bottom:1px solid var(--line)}
.tb-dtbl td{padding:8px;border-bottom:1px solid var(--line);vertical-align:top}
.tb-dtbl tr:last-child td{border-bottom:0}
.tb-mob,.tb-pin{display:block;color:var(--muted);font-size:11px;margin-top:1px}
.tb-moblink{color:var(--pinkink);text-decoration:none;font-weight:600}
.tb-moblink:hover{text-decoration:underline}
.tb-unassigned{color:var(--overdue);font-weight:600}
.tb-empty{color:var(--muted);text-align:center;padding:14px}
.tb-stpill{display:inline-block;padding:1px 8px;border-radius:5px;font-size:11px;font-weight:700;text-transform:capitalize}
.tb-st-done{color:var(--done);background:var(--donebg)}
.tb-st-open{color:var(--open);background:var(--openbg)}
.tb-st-overdue{color:#fff;background:var(--overdue)}
.tb-st-cancelled{color:var(--cancel);background:var(--cancelbg)}
.tb-tagun{color:var(--overdue);background:var(--overduebg)}

.tb-kpis5{grid-template-columns:repeat(5,1fr)}
.tb-exgrid{display:grid;grid-template-columns:1.3fr 1fr;gap:16px;margin-top:22px}
.tb-panel{background:var(--card);border:1px solid var(--line);border-radius:16px;padding:16px 18px;box-shadow:0 1px 2px rgba(36,19,38,.04),0 6px 18px rgba(36,19,38,.05)}
.tb-ph{font-size:12px;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);font-weight:700;margin-bottom:10px}
.tb-tbl{width:100%;border-collapse:collapse;font-size:13.5px}
.tb-tbl th{font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);font-weight:700;text-align:left;padding:6px 8px;border-bottom:1px solid var(--line)}
.tb-tbl td{padding:9px 8px;border-bottom:1px solid var(--line)}
.tb-tbl tr:last-child td{border-bottom:0}
.tb-hotrow{background:rgba(232,89,12,.05)}
.tb-pill{display:inline-block;padding:2px 8px;border-radius:6px;font-size:11.5px;font-weight:700}
.tb-pok{color:var(--done);background:var(--donebg)} .tb-pwarn{color:var(--overdue);background:var(--overduebg)} .tb-pbad{color:#fff;background:var(--overdue)}
.tb-trend{display:flex;gap:10px;align-items:flex-end;height:160px;padding-top:6px}
.tb-tcol{flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;height:100%}
.tb-tbar{flex:1;width:100%;max-width:38px;display:flex;align-items:flex-end}
.tb-tfill{width:100%;background:#c4b7cb;border-radius:6px 6px 0 0;position:relative;display:flex;align-items:flex-end;min-height:3px}
.tb-tover{width:100%;background:var(--overdue);border-radius:6px 6px 0 0}
.tb-tnum{font-size:12px;font-weight:700}
.tb-tlab{font-size:10.5px;color:var(--muted)}
.tb-tleg{display:flex;gap:16px;font-size:12px;color:var(--muted);margin-top:10px}
.tb-tleg span{display:inline-flex;align-items:center;gap:6px}

@media (prefers-color-scheme:dark){.tb-root{--bg:#17121b;--card:#211b26;--ink:#f3edf4;--muted:#a393ab;--line:#2f2736;--soft:#1c1621;--track:#2b2331;--done:#3bbd77;--open:#5a8bf0;--overdue:#ff7a3d;--atrisk:#f0b429;--cancel:#9a8da1;--pinkink:#ff7fb6}}
@media (max-width:820px){.tb-kpis,.tb-kpis5{grid-template-columns:repeat(2,1fr)} .tb-exgrid{grid-template-columns:1fr}
  .tb-row{grid-template-columns:1fr 60px;row-gap:12px} .tb-barwrap,.tb-split,.tb-ot{grid-column:1 / -1} .tb-ot{text-align:left} .tb-split{flex-direction:row;gap:22px} .tb-rhead{display:none} .tb-tot{order:-1;text-align:left}}
`;
